import { Link, useParams } from "react-router-dom";
import { newsList } from "@/mocks/news";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/feature/EmptyState";
import useDocumentMeta from "@/hooks/useDocumentMeta";

export default function NewsDetail() {
  const { id } = useParams();
  const news = newsList.find((item) => item.id === id);

  useDocumentMeta(
    news ? `${news.title} - 娱乐资讯 - 色花堂` : "资讯不存在 - 色花堂",
    news?.summary,
  );

  if (!news) {
    return (
      <div className="px-4 py-16 md:px-8">
        <div className="mx-auto w-full max-w-2xl">
          <EmptyState
            icon="ri-error-warning-line"
            title="没有找到这篇资讯"
            description="该资讯可能已被移除或链接有误，返回首页看看其他精彩内容吧。"
            actionLabel="返回首页"
            actionTo="/"
          />
        </div>
      </div>
    );
  }

  const related = newsList.filter((item) => item.category === news.category && item.id !== news.id).slice(0, 3);

  return (
    <article>
      <div className="border-b border-background-200 bg-background-50">
        <div className="mx-auto w-full max-w-[900px] px-4 py-6 md:px-8 md:py-8">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "娱乐资讯", to: "/news" },
              { label: news.title },
            ]}
          />
          <div className="mt-5 flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded-full bg-primary-500 px-3 py-1 font-medium text-background-50">
              {news.category}
            </span>
            <Link
              to="/news"
              className="rounded-full bg-background-100 px-3 py-1 text-foreground-600 transition-colors hover:text-primary-700 cursor-pointer"
            >
              娱乐资讯
            </Link>
          </div>
          <h1 className="mt-4 font-heading text-2xl font-extrabold leading-tight text-foreground-950 md:text-[2.1rem]">
            {news.title}
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line text-base" aria-hidden="true"></i>
              {news.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line text-base" aria-hidden="true"></i>
              {news.date}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line text-base" aria-hidden="true"></i>
              {news.views.toLocaleString()} 次阅读
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-newspaper-line text-base" aria-hidden="true"></i>
              来源：{news.source}
            </span>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[900px] px-4 py-8 md:px-8">
        <figure className="overflow-hidden rounded-lg border border-background-200">
          <div className="h-56 w-full md:h-96">
            <img
              src={news.cover}
              alt={news.title}
              title={`${news.title} - 色花堂娱乐资讯`}
              className="h-full w-full object-cover object-top"
            />
          </div>
          <figcaption className="bg-background-50 px-4 py-3 text-xs text-foreground-500">
            配图：{news.title}（示范图片）
          </figcaption>
        </figure>

        <div className="mt-8 flex flex-col gap-5">
          {news.content.map((paragraph, idx) => (
            <p key={idx} className="text-base leading-loose text-foreground-800">
              {paragraph}
            </p>
          ))}
        </div>

        <div className="mt-8 flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-600">
            <i className="ri-play-circle-line text-2xl" aria-hidden="true"></i>
          </span>
          <div>
            <h2 className="font-heading text-sm font-bold text-foreground-950">相关视频</h2>
            <p className="mt-1 text-xs text-foreground-500">
              该内容为演示示例，本站暂未集成真实视频播放功能。
            </p>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-2">
          <span className="text-sm text-foreground-500">相关标签：</span>
          {news.tags.map((tag) => (
            <span
              key={tag}
              className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800"
            >
              #{tag}
            </span>
          ))}
        </div>

        <div className="mt-8">
          <Link
            to="/news"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700 cursor-pointer"
          >
            <i className="ri-arrow-left-line text-base" aria-hidden="true"></i>
            返回娱乐资讯
          </Link>
        </div>
      </div>

      {related.length > 0 && (
        <section className="border-t border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-8">
            <h2 className="mb-6 font-heading text-xl font-extrabold text-foreground-950">相关推荐</h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {related.map((item) => (
                <Link
                  key={item.id}
                  to={`/news/detail/${item.id}`}
                  className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300 cursor-pointer"
                >
                  <div className="h-40 w-full overflow-hidden">
                    <img
                      src={item.cover}
                      alt={item.title}
                      title={`${item.title} - 色花堂娱乐资讯`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="line-clamp-2 font-heading text-sm font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
                      {item.title}
                    </h3>
                    <p className="mt-2 text-xs text-foreground-500">{item.date}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}